import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../supabase';
import { getTemplateForTest } from '../constants/LabTemplates';
import Navbar from '../components/Navbar';
import './LabReportPortal.css';

function LabReportPortal() {
    const { id } = useParams();
    const [prescription, setPrescription] = useState(null);
    const [clinic, setClinic] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        async function fetchData() {
            try {
                const { data: rxData, error: rxError } = await supabase
                    .from('prescriptions')
                    .select('*, clinics(*)')
                    .eq('id', id)
                    .single();

                if (rxError) throw rxError;
                setPrescription(rxData);
                if (rxData.clinics) setClinic(rxData.clinics);

            } catch (err) {
                console.error(err);
                setError('Report not found or invalid link.');
            } finally {
                setLoading(false);
            }
        }
        fetchData();
    }, [id]);

    if (loading) {
        return (
            <>
                <Navbar />
                <div className="portal-loading">
                    <div className="spinner" style={{ width: '40px', height: '40px', border: '4px solid #f3f3f3', borderTop: '4px solid var(--primary-500)', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                    <p style={{ marginTop: '1rem', color: '#6b7280' }}>Loading your report...</p>
                </div>
                <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            </>
        );
    }

    if (error || !prescription) {
        return (
            <>
                <Navbar />
                <div className="portal-error">
                    <h2>Report Not Found</h2>
                    <p>The link might be invalid or the prescription was removed.</p>
                </div>
            </>
        );
    }

    const isPending = ['pending', 'sample_collected', 'in_progress_lab'].includes(prescription.lab_status);

    const getStatusText = (status) => {
        switch (status) {
            case 'pending': return 'Awaiting Sample';
            case 'sample_collected': return 'Sample Collected';
            case 'in_progress_lab': return 'Testing In Progress';
            default: return 'Processing...';
        }
    };

    const renderResults = () => {
        const raw = prescription.lab_results;
        if (!raw) return null;

        let parsed = null;
        let isStructured = false;

        try {
            parsed = JSON.parse(raw);
            if (parsed.structured) isStructured = true;
        } catch {
            // it's plain text
            return (
                <div className="portal-results-text">
                    <pre>{raw}</pre>
                </div>
            );
        }

        if (parsed.mode === 'upload') {
            const isPdf = parsed.fileName.toLowerCase().endsWith('.pdf');
            return (
                <div className="portal-results-upload">
                    {isPdf ? (
                        <iframe src={parsed.url} title={parsed.fileName} className="portal-pdf-viewer"></iframe>
                    ) : (
                        <img src={parsed.url} alt="Lab Report" className="portal-image-viewer" />
                    )}
                    <div className="portal-download-bar no-print">
                        <a href={parsed.url} download className="btn-primary" target="_blank" rel="noopener noreferrer">Download Report</a>
                    </div>
                </div>
            );
        }

        if (isStructured && parsed.data) {
            return (
                <div className="portal-structured-results">
                    {Object.keys(parsed.data).map(testGroup => {
                        const template = getTemplateForTest(testGroup);
                        const groupData = parsed.data[testGroup];
                        return (
                            <div key={testGroup} className="portal-test-group">
                                <h3>{testGroup}</h3>
                                <table className="portal-results-table">
                                    <thead>
                                        <tr>
                                            <th>Parameter</th>
                                            <th>Result</th>
                                            <th>Reference Range</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Object.keys(groupData).map(key => {
                                            const pDef = template?.find(p => p.key === key);
                                            const val = groupData[key];
                                            const numVal = parseFloat(val);
                                            const isLow = pDef && !isNaN(numVal) && numVal < pDef.min;
                                            const isHigh = pDef && !isNaN(numVal) && numVal > pDef.max;
                                            const isFlagged = isLow || isHigh;

                                            return (
                                                <tr key={key} className={isFlagged ? 'flagged-row' : ''}>
                                                    <td>{pDef ? pDef.name : key}</td>
                                                    <td className="result-value">
                                                        <strong>{val}</strong> {isFlagged && <span className="flag-badge">{isLow ? 'L' : 'H'}</span>}
                                                        {pDef?.unit && <span className="unit"> {pDef.unit}</span>}
                                                    </td>
                                                    <td className="ref-range">{pDef ? `${pDef.min} - ${pDef.max} ${pDef.unit}` : '-'}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        );
                    })}
                </div>
            );
        }

        return null;
    };

    return (
        <>
            <Navbar />
            <div className="lab-portal-container">
                <div className="portal-paper">
                    {clinic ? (
                        <div className="portal-header">
                            {clinic.logo_url && <img src={clinic.logo_url} alt="Clinic Logo" className="portal-logo" />}
                            <div className="portal-clinic-info">
                                <h1>{clinic.name}</h1>
                                <p>{clinic.address}</p>
                                <p>Phone: {clinic.phone}</p>
                            </div>
                        </div>
                    ) : (
                        <div className="portal-header">
                            <div className="portal-clinic-info">
                                <h1>Klinik Partner</h1>
                            </div>
                        </div>
                    )}

                    <hr className="portal-divider" />

                    <div className="portal-patient-info">
                        <div className="info-item"><span>Patient</span><strong>{prescription.patient_name}</strong></div>
                        <div className="info-item"><span>Age/Gender</span><strong>{prescription.patient_age} Y / {prescription.patient_gender}</strong></div>
                        <div className="info-item"><span>Prescription Date</span><strong>{new Date(prescription.created_at).toLocaleDateString()}</strong></div>
                        <div className="info-item"><span>Prescribed By</span><strong>Dr. {prescription.doctor_name}</strong></div>
                        <div className="info-item"><span>Prescription ID</span><strong>{prescription.id.split('-')[0].toUpperCase()}</strong></div>
                    </div>

                    <h2 className="portal-title">
                        Laboratory Test Report
                        {(() => {
                            if (!prescription.lab_results) return null;
                            try {
                                const parsed = JSON.parse(prescription.lab_results);
                                if (parsed.is_edited) {
                                    return (
                                        <span style={{ marginLeft: '0.5rem', fontSize: '1rem', fontWeight: 'bold', color: '#ea580c', background: '#ffedd5', padding: '0.2rem 0.6rem', borderRadius: '4px', verticalAlign: 'middle' }}>
                                            (Edited Report)
                                        </span>
                                    );
                                }
                            } catch(e) {}
                            return null;
                        })()}
                    </h2>

                    {isPending ? (
                        <div className="portal-pending-state">
                            <div className="animated-progress">
                                <div className="progress-bar"></div>
                            </div>
                            <h3>Your tests are being processed</h3>
                            <p>Current Status: <strong>{getStatusText(prescription.lab_status)}</strong></p>
                            <p className="refresh-note">This page will be updated automatically when your results are ready. Please check back later.</p>
                        </div>
                    ) : (
                        <div className="portal-results-section">
                            {renderResults()}
                        </div>
                    )}

                    <div className="portal-footer no-print">
                        {!isPending && (
                            <button className="btn-secondary print-report-btn" onClick={() => window.print()}>Print / Save PDF</button>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

export default LabReportPortal;
